import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { TrashIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { ROUTES } from '../../consts/general';
import ReportTemplate from '../../components/reports/ReportTemplate';
import DateRangePicker from '../../components/reports/DateRangePicker';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { deleteCustomReport, getCustomReports } from '../../slices/customReports';
import { CustomReportColumnsEditModal } from '../../components/reports/CustomReportColumnsEditModal';
import { formatMetaCurrency } from './metaReportHelpers';
import useDateRangeParams from '../../hooks/useDateRangeParams';

const DEFAULT_END_DATE = new Date().toISOString().split('T')[0];
const DEFAULT_START_DATE = (() => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date.toISOString().split('T')[0];
})();

export const formatCell = (value, type) => {
  if (value === null || value === undefined || value === '') return '—';
  if (type === 'currency') return formatMetaCurrency(value);
  if (type === 'number' && typeof value === 'number') return value.toLocaleString();
  if (type === 'percentage' && typeof value === 'number') return `${value}%`;
  return String(value);
};

/** Generic page that renders any saved KIP-built custom report. */
const CustomReport = ({ marginFromSidebar }) => {
  const { customReportId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { reports: customReportsList, isLoaded } = useSelector((s) => s.customReports);

  const definition = useMemo(
    () => customReportsList.find((r) => r.id === customReportId) || null,
    [customReportsList, customReportId],
  );

  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [reportMeta, setReportMeta] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { startDate, endDate, setDateRange } = useDateRangeParams(DEFAULT_START_DATE, DEFAULT_END_DATE);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isEditColumnsOpen, setIsEditColumnsOpen] = useState(false);
  const [reportRefreshKey, setReportRefreshKey] = useState(0);
  const pageSize = 100;
  const previousCustomReportId = useRef(customReportId);

  useEffect(() => {
    if (!isLoaded) {
      dispatch(getCustomReports());
    }
  }, [isLoaded, dispatch]);

  useEffect(() => {
    if (previousCustomReportId.current === customReportId) return;
    previousCustomReportId.current = customReportId;
    setDateRange({ startDate: DEFAULT_START_DATE, endDate: DEFAULT_END_DATE });
  }, [customReportId]);

  useEffect(() => {
    if (!customReportId || !startDate || !endDate) return;
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          startDate,
          endDate,
          page: String(currentPage),
          pageSize: String(pageSize),
        });
        if (sortField) {
          params.set('sortField', sortField);
          params.set('sortDirection', sortDirection || 'asc');
        }
        const res = await fetch(`/api/v1/custom-reports/${customReportId}/run?${params.toString()}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json?.error || 'Failed to load report');
        if (cancelled) return;
        setRows(json.rows || []);
        setColumns(json.columns || []);
        setReportMeta(json.report || null);
        setTotalRecords(json.totalRecords || 0);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load report');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [customReportId, startDate, endDate, currentPage, sortField, sortDirection, reportRefreshKey]);

  const handleDateChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
    setDateRange({ startDate: newStartDate, endDate: newEndDate });
    setCurrentPage(1);
  };

  const handleSort = ({ field, direction }) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const handleDelete = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this custom report? This cannot be undone.')) return;
    try {
      await dispatch(deleteCustomReport(customReportId)).unwrap();
      toast.success('Report deleted', { position: 'bottom-right' });
      navigate(ROUTES.REPORTS);
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to delete report', { position: 'bottom-right' });
    }
  };

  /** Every page in the window — the CSV must never be the visible page silently passed off as the whole report. */
  const fetchAllRows = async () => {
    const collected = [];
    for (let page = 1; ; page += 1) {
      const params = new URLSearchParams({
        startDate,
        endDate,
        page: String(page),
        pageSize: String(pageSize),
      });
      if (sortField) {
        params.set('sortField', sortField);
        params.set('sortDirection', sortDirection || 'asc');
      }
      // eslint-disable-next-line no-await-in-loop
      const res = await fetch(`/api/v1/custom-reports/${customReportId}/run?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      // eslint-disable-next-line no-await-in-loop
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || 'Failed to export report');
      const pageRows = json.rows || [];
      collected.push(...pageRows);
      const total = json.totalRecords || collected.length;
      if (pageRows.length === 0 || collected.length >= total) return collected;
    }
  };

  const handleExportCSV = async () => {
    let exportRows = rows;
    try {
      exportRows = await fetchAllRows();
    } catch (e) {
      toast.error(e.message || 'Failed to export the full report', { position: 'bottom-right' });
      // `handled` keeps CSVExportButton from falling back to its own client-side export, which
      // would report "No data available" over the top of the real error.
      return { handled: true };
    }
    const header = columns.map((c) => c.label).join(',');
    const lines = exportRows.map((row) =>
      columns
        .map((c) => {
          const raw = row?.[c.key];
          const value = raw === null || raw === undefined ? '' : String(raw);
          return `"${value.replace(/"/g, '""')}"`;
        })
        .join(','),
    );
    const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(definition?.name || 'custom-report').replace(/[^a-z0-9-_]+/gi, '-').toLowerCase()}-${startDate}-${endDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    return { handled: true };
  };

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  const title = definition?.name || reportMeta?.name || 'Custom Report';
  const description =
    definition?.description
    || (reportMeta?.baseReportName ? `Custom report built with Kip from "${reportMeta.baseReportName}".` : 'Custom report built with Kip.');

  const breadcrumbs = [
    { label: 'Reports', path: ROUTES.REPORTS },
    { label: title },
  ];

  const filtersComponent = (
    <DateRangePicker
      startDate={startDate}
      endDate={endDate}
      onDateChange={handleDateChange}
      label="Date Range"
      error={null}
    />
  );

  const exportButton = (
    <div className="flex items-center space-x-2">
      <CSVExportButton
        data={[]}
        columns={columns}
        filename={definition?.name || 'custom-report'}
        onExport={handleExportCSV}
        disabled={isLoading || rows.length === 0}
        variant="outline"
      />
      <button
        type="button"
        onClick={() => setIsEditColumnsOpen(true)}
        aria-label="Edit columns"
        title="Edit columns"
        className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
      >
        Edit columns
      </button>
      <button
        type="button"
        onClick={handleDelete}
        aria-label="Delete report"
        title="Delete report"
        className="text-gray-400 hover:text-red-600"
      >
        <TrashIcon className="h-5 w-5" />
      </button>
    </div>
  );

  const paginationComponent = totalPages > 1 ? (
    <div className="flex items-center justify-between">
      <div className="text-sm text-gray-700">
        Page {currentPage} of {totalPages} ({totalRecords.toLocaleString()} records)
      </div>
      <div className="flex items-center space-x-2">
        <button
          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          disabled={currentPage === 1}
          className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          Previous
        </button>
        <button
          onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages}
          className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          Next
        </button>
      </div>
    </div>
  ) : null;

  if (isLoaded && !definition) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 px-6 text-center">
        <h2 className="text-lg font-semibold text-gray-900">Custom report not found</h2>
        <p className="text-sm text-gray-500">It may have been deleted.</p>
        <button
          type="button"
          onClick={() => navigate(ROUTES.REPORTS)}
          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm hover:bg-gray-50"
        >
          Back to reports
        </button>
      </div>
    );
  }

  return (
    <>
      <ReportTemplate
        title={title}
        description={description}
        breadcrumbs={breadcrumbs}
        marginFromSidebar={marginFromSidebar}
        onBack={() => navigate(ROUTES.REPORTS)}
        isLoading={isLoading}
        error={error}
        filters={filtersComponent}
        exportButton={exportButton}
        data={rows}
        columns={columns}
        pagination={paginationComponent}
        emptyMessage="No data found for the selected date range."
        onSort={handleSort}
        sortField={sortField}
        sortDirection={sortDirection}
        enableSorting={true}
      >
        {rows.map((row, index) => (
          <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
            {columns.map((column, colIndex) => (
              <td
                key={column.key}
                className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${
                  column.type === 'number' || column.type === 'currency' ? 'text-right' : ''
                } ${colIndex === 0 ? (index % 2 === 0 ? 'bg-white' : '!bg-gray-50') : ''}`}
              >
                {formatCell(row[column.key], column.type)}
              </td>
            ))}
          </tr>
        ))}
      </ReportTemplate>
      <CustomReportColumnsEditModal
        isOpen={isEditColumnsOpen}
        cancelClick={() => setIsEditColumnsOpen(false)}
        report={definition}
        marginFromSidebar={marginFromSidebar}
        onSaved={() => setReportRefreshKey((current) => current + 1)}
      />
    </>
  );
};

export default CustomReport;
